import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Assets
import ErrorLogo from "../Assets/Images/Icons/somethingWentWrongError.svg";

/** Environment */
export const IS_DEV = process.env.REACT_APP_IS_DEV;

/** Client URL */
export const CLIENT_URL = process.env.REACT_APP_BASE_URL;

/** API URL */
export const API_VERSION = process.env.REACT_APP_API_VERSION;
export const API_URL = process.env.REACT_APP_API_URL;
export const Image_URL = process.env.REACT_APP_API_URL;
export const AUTH_ACCESS_TOKEN = process.env.REACT_APP_API_ACCESS_TOKEN;
export const BUCKET_URL = process.env.REACT_APP_BUCKET_URL;
export const PROFILE_URL = process.env.REACT_APP_BUCKET_PROFILE_URL;

/** Common Variables */
export const EMAIL_REGEX = /^[\w+-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
export const PHONE_REGEX =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
export const ONLY_NUMBERS_REGEX = /^[0-9]+$/;
export const ONLY_ALPHABETS_REGEX = /^[A-Za-z ]+$/;

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/** Date Functions Starts */
const currentYear = new Date().getFullYear();
const startOfYear = new Date(currentYear, 0, 1);
export const dateFormat = "YYYY-MM-DD";
export const dateFormat2 = "YYYY/MM/DD";
export const dateFormatTIME = "YYYY/MM/DD hh:mm A";
export const dayjsFormat = "YYYY-MM-DD";
export const momentDDMMYY = (data) => moment(data).format(dateFormat);
export const momentDDMMYY2 = (data) => moment(data).format(dateFormat2);
export const momentDDMMYYTIME = (data) => moment(data).format(dateFormatTIME);
export const lifetimeDate = new Date(dayjs(startOfYear).format(dateFormat));
/** Date Functions Ends */

export function convertToUTC(dateRange) {
  const { startDate, endDate } = dateRange;

  // Automatically get the browser's local timezone
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const startUTC = dayjs
    .tz(startDate, dateFormat, localTz)
    .startOf("day")
    .utc()
    .toISOString();

  const endUTC = dayjs
    .tz(endDate, dateFormat, localTz)
    .endOf("day")
    .utc()
    .toISOString();

  return {
    startDate: startUTC,
    endDate: endUTC,
  };
}

/** Error Handling Functions Starts */
export const dispatchLoading = (dispatch, scope, status) =>
  dispatch({ type: "SET_LOADING", payload: { scope, status } });

export const dispatchToast = (dispatch, scope, status) =>
  dispatch({ type: "SET_TOAST", payload: { scope, status } });

export const dispatchError = (dispatch, scope, status) =>
  dispatch({ type: "SET_ERROR", payload: { scope, status } });

export const catchHandler = (dispatch, scope) => (err) => {
  let errMsg = err?.response?.data?.msg
    ? err?.response?.data?.msg
    : err?.response?.data?.message
    ? err?.response?.data?.message
    : err?.response?.data?.error
    ? err?.response?.data?.error
    : "Something went wrong";
  dispatchToast(dispatch, "error", errMsg);
  if (err.code === "ERR_NETWORK")
    dispatchToast(dispatch, "error", "Unable to connect to server");
  else if (err?.response?.status !== 401) {
    dispatchError(dispatch, scope, [errMsg]);
  } else if (err?.response?.status === 401) {
    localStorage.setItem("isLoggedIn", false);
    dispatchError(dispatch, scope, [errMsg]);
    // window.location.assign(`${CLIENT_URL}/auth/login`);
  } else dispatchToast(dispatch, "error", errMsg);
  dispatchLoading(dispatch, scope, false);
  dispatchError(dispatch, scope, [errMsg]);
};

export const elseHandler = (dispatch, scope, data) => {
  dispatchToast(dispatch, "error", data?.msg);
  dispatchError(dispatch, scope, data?.error);
};

export const ErrorFallbackPage = ({ error, resetErrorBoundary }) => {
  // Call resetErrorBoundary() to reset the error boundary and retry the render.

  // Use Navigate
  const navigate = useNavigate();

  // Use Translation
  const { t } = useTranslation();

  // Use location
  const { pathname } = useLocation();

  // Use Ref
  const originalPathname = useRef(pathname);

  // Goto previous Page
  const goToBack = () => {
    navigate(-1);
  };

  // Use Effect
  useEffect(() => {
    if (pathname !== originalPathname.current) resetErrorBoundary();
  }, [pathname, resetErrorBoundary]);

  return (
    <div
      className="text-center w-100 d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}
    >
      <div>
        <div className="px-3 mb-4 header-logo">
          <img
            src={ErrorLogo}
            alt="header logo"
            className="img-fluid"
            onClick={(e) => navigate("/")}
          />
        </div>
        <h5 className="ff_rg mb-1 f18">{t("error.somethingWrong")}</h5>
        <p className="ff_rg f14">
          {t("error.thereWasProblem")}
          <span className="d-block ff_rg f14 mx-3">
            {t("error.pleaseRefresh")}
          </span>
        </p>
        <p className="ff_rg f14 mt-3 text-secondary">
          {t("error.somethingNot")} ?{" "}
          <span className="ff_md f14 text-dark">
            {t("error.contact")} :{" "}
            <Link
              className="ff_md f14 text-dark"
              to="mailto: support@companyname.com"
            >
              support@companyname.com
            </Link>{" "}
          </span>
        </p>
        <button
          className="btn btn-primary text-uppercase fs-6 px-3 txt-white mt-3"
          onClick={() => goToBack()}
        >
          {t("error.goBack")}
        </button>
      </div>
    </div>
  );
};
/** Error Handling Functions Ends */

/**
 * Protected Route for Client
 * @param {Object} routeComponent - Object with route data
 * @param {String} routeComponent.roleName - Role of user
 * @param {Function} routeComponent.children - Child component / page
 * @returns {Function} Child Component
 */
export const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const authSessionKey = localStorage.getItem("authorization");

  useEffect(() => {
    if (!authSessionKey) {
      navigate("/");
    }
  }, [authSessionKey, navigate]);

  if (authSessionKey) {
    return <>{children}</>;
  }

  // Optionally return null or a loading spinner while checking the authSessionKey
  return null;
};

export default ProtectedRoute;

/** Loader */
export const SiteLoader = ({ text }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  if (pathname === "/") {
    return null;
  }
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        textAlign: "center",
        width: "calc(100% - 1rem)",
      }}
    >
      <div className="Loader">
        <div className="d-block">
          <div className="spinner-border" role="status"></div>
        </div>
      </div>
      <p style={{ fontSize: "1.5rem", lineHeight: "1.1" }}>
        {text !== undefined ? text : t("pleaseWait")}
      </p>
    </div>
  );
};

/** Reset Function to remove data after login */
export const Reset = () => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  localStorage.removeItem("userAccessToken");
  window.location.href = baseURL; // Redirect to home page
};

/** Global Button Loader */
export const BtnLoader = () => {
  return (
    <Spin
      indicator={
        <LoadingOutlined
          style={{
            fontSize: 16,
            color: "#fff",
          }}
          spin
          className="me-2"
        />
      }
    />
  );
};

/** Button Loader */
export const ButtonLoader = ({
  className,
  onClick,
  loader,
  title,
  disable,
  type,
}) => {
  return (
    <button
      className={`btn ${className}`}
      onClick={onClick}
      disabled={disable || loader}
      type={type}
    >
      {title} {loader && <BtnLoader />}
    </button>
  );
};
